import { FunctionComponent, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { KidMarker } from '../../../../../../api/kids/types';
import { KidDocumentForm, PdfElement } from '../../types/KidPdfGeneratorV2.types';

interface TableInputSimpleProps {
  index: number;
  field: PdfElement;
  markers?: KidMarker[];
}

const CELL_MARKER_REGEX = /^\[\[([A-Z_]+):([a-z0-9_]+)\]\]$/;

const TableInputSimple: FunctionComponent<TableInputSimpleProps> = ({
  field,
  index,
  markers = [],
}) => {
  const { setValue, getValues } = useFormContext<KidDocumentForm>();
  const [selectedMarkerByCell, setSelectedMarkerByCell] = useState<
    Record<string, string>
  >({});

  // Need for now till i master the rendering cycles of the formstate
  const rawContent = field.content;

  const markerOptions = useMemo(
    () =>
      markers.map((marker) => ({
        token: `[[${marker.marker_type}:${marker.placeholder}]]`,
        label: `${marker.marker_type} | ${marker.placeholder} | ${marker.value}`,
      })),
    [markers],
  );

  if (!Array.isArray(rawContent)) return null;

  const content = rawContent as string[][];

  const updateCellValue = (
    rowIndex: number,
    cellIndex: number,
    newValue: string,
  ) => {
    const currentContent = (getValues(`elements.${index}.content` as const) ||
      content) as string[][];

    const updatedContent = currentContent.map((row, r) =>
      row.map((cell, c) => (r === rowIndex && c === cellIndex ? newValue : cell)),
    );

    setValue(`elements.${index}.content` as any, updatedContent, {
      shouldDirty: true,
    });
  };

  const getMarkerFromCellValue = (value: string): KidMarker | null => {
    const match = value.match(CELL_MARKER_REGEX);
    if (!match) return null;

    const markerType = match[1];
    const placeholder = match[2];
    return (
      markers.find(
        (marker) =>
          marker.marker_type === markerType && marker.placeholder === placeholder,
      ) || null
    );
  };

  const getCellKey = (rowIndex: number, cellIndex: number) =>
    `${rowIndex}-${cellIndex}`;

  const handleMarkerSelect = (
    rowIndex: number,
    cellIndex: number,
    markerToken: string,
  ) => {
    const cellKey = getCellKey(rowIndex, cellIndex);
    setSelectedMarkerByCell((prev) => ({
      ...prev,
      [cellKey]: markerToken,
    }));
  };

  const addSelectedMarkerToCell = (rowIndex: number, cellIndex: number) => {
    const cellKey = getCellKey(rowIndex, cellIndex);
    const selectedMarker = selectedMarkerByCell[cellKey];
    if (!selectedMarker) return;

    const match = selectedMarker.match(CELL_MARKER_REGEX);
    if (match) {
      const markerType = match[1];
      const placeholder = match[2];
      const markerToAdd = markers.find(
        (marker) =>
          marker.marker_type === markerType && marker.placeholder === placeholder,
      );

      if (markerToAdd) {
        const currentExistingMarkers =
          (getValues(`elements.${index}.existing_markers` as const) as
            | KidMarker[]
            | undefined) || [];

        const alreadyExists = currentExistingMarkers.some(
          (marker) =>
            marker.marker_type === markerToAdd.marker_type &&
            marker.placeholder === markerToAdd.placeholder,
        );

        if (!alreadyExists) {
          setValue(
            `elements.${index}.existing_markers` as any,
            [...currentExistingMarkers, markerToAdd],
            { shouldDirty: true },
          );
        }
      }
    }

    updateCellValue(rowIndex, cellIndex, selectedMarker);
  };

  const removeMarkerFromCell = (rowIndex: number, cellIndex: number) => {
    updateCellValue(rowIndex, cellIndex, '');
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-300">
        <tbody>
          {content.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="border border-gray-300 px-2 py-1 align-top"
                  style={{ width: `${100 / row.length}%` }}
                >
                  {(() => {
                    const marker = getMarkerFromCellValue(cell);
                    const cellKey = getCellKey(rowIndex, cellIndex);
                    const selectedMarker = selectedMarkerByCell[cellKey] || '';

                    if (marker) {
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800">
                            {marker.marker_type} | {marker.placeholder} | {marker.value}
                          </div>
                          <button
                            type="button"
                            className="self-start rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50"
                            onClick={() => removeMarkerFromCell(rowIndex, cellIndex)}
                          >
                            Remove marker
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="flex flex-col gap-1">
                        <input
                          className="w-full border-none bg-transparent p-0 text-sm text-gray-800 outline-none focus:ring-0"
                          value={cell}
                          onChange={(e) =>
                            updateCellValue(rowIndex, cellIndex, e.target.value)
                          }
                        />
                        <div className="flex items-center gap-1">
                          <select
                            className="min-w-0 flex-1 rounded border border-gray-300 px-1 py-0.5 text-xs"
                            value={selectedMarker}
                            onChange={(e) =>
                              handleMarkerSelect(rowIndex, cellIndex, e.target.value)
                            }
                          >
                            <option value="">Select marker</option>
                            {markerOptions.map((option) => (
                              <option key={option.token} value={option.token}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50"
                            disabled={!selectedMarker}
                            onClick={() => addSelectedMarkerToCell(rowIndex, cellIndex)}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableInputSimple;